/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';
import { isNil } from 'lodash';
import idx from 'idx';

import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupCreationLimitNotice from './GroupCreationLimitNotice';

import USER_PRODUCT_LIMITS_QUERY from 'groups/queries/UserProductLimitsForOrgQuery.gql';
import type { UserProductLimitsForOrg } from 'groups/queries/__generated__/UserProductLimitsForOrg';

export type Props = {
  selectedOrgUuid: ?string,
  groupCode: ?string,
  group: ?Group,
  context: 'edit' | 'create',
  data: ReactApolloData<UserProductLimitsForOrg>,
};

class GroupCreationLimitNoticeContainer extends React.Component<Props> {
  render() {
    const currentUser = CurrentUser.isSignedIn() && CurrentUser.getUser();
    const hasFreeProductLimitFeature =
      currentUser && currentUser.hasFeature('free_product_limits.web.apr-19');
    if (!hasFreeProductLimitFeature) {
      return null;
    }

    const { data, group, selectedOrgUuid, groupCode } = this.props;
    const org = group && group.getOrganization();
    const existingOrgAssociation = org && org.get('uuid');
    const groupHasOrg = !(isNil(existingOrgAssociation) && isNil(selectedOrgUuid));
    const userHasChangedOrg = groupHasOrg && existingOrgAssociation !== selectedOrgUuid;
    const numberOfClassesLimit = idx(
      data,
      _ => _.productLimits.userProductLimits.numberOfClassesLimit
    );
    if (data.loading || !numberOfClassesLimit) {
      return null;
    }

    if (data.error) {
      return (
        <ErrorReporter error="GroupCreationLimitNoticeContainer - failed to fetch userProductLimits" />
      );
    }

    if (group && !userHasChangedOrg) {
      // if the user is editing an existing group and has not modified the org association,
      // show warning based on whether the group currently has limited send functionality
      return (
        <GroupCreationLimitNotice
          showWarning={group.get('hasLimitedSendFunctionality')}
          numberOfClassesLimit={numberOfClassesLimit}
          groupCode={groupCode}
          isExistingGroup
        />
      );
    }

    // otherwise, show the warning based on GQL product limits data using the newly
    // selected org association
    return (
      <GroupCreationLimitNotice
        showWarning={idx(
          data,
          i => i.productLimits.userProductLimits.willExceedNumberOfClassesLimit
        )}
        numberOfClassesLimit={numberOfClassesLimit}
        groupCode={groupCode}
        isExistingGroup={Boolean(group)}
      />
    );
  }
}

export { GroupCreationLimitNoticeContainer };

const withLimits = graphql(USER_PRODUCT_LIMITS_QUERY, {
  options: ({ selectedOrgUuid }) => ({
    variables: { orgUuid: selectedOrgUuid },
    fetchPolicy: 'cache-and-network',
  }),
});

export default withLimits(GroupCreationLimitNoticeContainer);
